import { currentURL, parseQuery } from '../shared/url'
import { Storages } from './storage'
import { WidgetRuntime } from './runtime'

export interface UTM {
  utm_source?: string
  utm_medium?: string
  utm_term?: string
  utm_content?: string
  utm_campaign?: string
  method?: string
}

const enum UTMMethod {
  Url = 'url',
  Referrer = 'referrer',
}

export interface Visit {
  userId: string
  sessionId: string
  sessionStartTime: number

  time: number
  location: string
  referrer: string
  pageNumber: number
  sessionNumber: number
  lastVisit: number

  source: string
  utm: UTM
  language: string
  displayWidth: number
  displayHeight: number
}

export interface SessionLoadResult {
  newUser: boolean
  newSession: boolean
}

interface SessionServerData {
  id?: string
  user_id?: string
  time?: number
  timedelta?: number
  token: string
  geo: {
    country?: string
    country_code?: string
    city?: string
    ip?: string
    geopath?: string
    latitude?: number
    longitude?: number
    postal_code?: string
    region?: string
    state_code?: string
    district?: string
    timezone?: string
  }
  ua: {
    platform?: string
    os?: string
    os_family?: string
    os_version?: string
    browser?: string
    browser_family?: string
    browser_version?: string
    device?: string
    device_brand?: string
    device_model?: string
  }
  utm?: {
    source?: string
    medium?: string
  }
}

export class Session {
  public userId: string
  public sessionId: string
  public sessionStartTime: Date

  private _visit: Visit
  private _serverData: Promise<SessionServerData>

  constructor(private storages: Storages) {}

  public isInitialized(): boolean {
    return !!this.sessionId
  }

  public loadOrCreate(location: WidgetRuntime['location'], sessionLength: number): SessionLoadResult {
    const lastVisit = this.storages.loadSession('lastVisit')
    const now = new Date().getTime()
    const isReturning = lastVisit && lastVisit < now - sessionLength * 60 * 1000
    if (isReturning) {
      this.storages.cleanupSession()
    }

    const result = { newUser: false, newSession: false }

    this.userId = this.storages.load('uid')
    if (this.userId && this.userId.length == 36) {
      // workaround: while changing local guid to global bigint user id
      this.userId = null
    }
    result.newUser = !this.userId
    if (result.newUser) {
      this.storages.cleanupSession()
    }

    this.sessionId = this.storages.loadSession('id')
    result.newSession = !this.sessionId
    if (result.newSession) {
      this.sessionStartTime = new Date()
      this.storages.newSession(this.sessionId, this.sessionStartTime)
      this.storages.saveSession('source', window.document.referrer)
      this.storages.saveSession('landing', location().href)

      this.storages.save('sessionsCount', (this.storages.load('sessionsCount') || 0) + 1)
    } else {
      this.sessionStartTime = new Date(this.storages.session.get('$start'))
    }

    return result
  }

  public updateServerData(url: string) {
    if (!this._serverData) {
      const saved = this.storages.loadSession('server')
      if (saved) {
        this._serverData = Promise.resolve(saved)
      } else {
        this._serverData = this.fetchServerData(url)
      }
    }
  }

  private fetchServerData(url: string): Promise<SessionServerData> {
    let source = this.source
    let query = ''
    const landing = this.landing
    let sourceUtm: SessionServerData['utm'] = {}

    if (source) {
      const cu = currentURL()
      let su
      try {
        su = new URL(source)
      } catch {
        source = ''
      }

      if (su && su.hostname != cu.hostname) {
        // not internal website referrer
        sourceUtm.medium = 'referrer'
        sourceUtm.source = su.hostname
      }
    }

    if (landing) {
      try {
        query = new URL(landing).search.replace('?', '')
      } catch {}
    }

    const u = new URL(url)
    if (this.userId) {
      u.searchParams.append('uid', this.userId)
    }
    if (this.sessionId) {
      u.searchParams.append('sid', this.sessionId)
    }
    if (source) {
      u.searchParams.append('source', source)
    }
    if (query) {
      u.searchParams.append('query', query)
    }

    return fetch(u.href)
      .then(resp => {
        if (!resp.ok) throw new Error(resp.statusText)
        return resp.json()
      })
      .then((data: SessionServerData) => {
        data.timedelta = data.time - new Date().getTime()
        console.info('got server session data: ', data)

        this.storages.saveSession('server', data)
        if (data.utm && Object.keys(data.utm).length) {
          sourceUtm = { ...data.utm }
        }
        this.refreshUtmServer(sourceUtm)
        if (data.id) {
          this.updateId(data.user_id, data.id)
        }

        return data
      })
      .catch(() => {
        return null
      })
  }

  public async getServerData(): Promise<SessionServerData> {
    const def = { timedelta: 0, token: '-', geo: {}, ua: {}, utm: {} }
    if (!this._serverData) {
      console.warn('server session data not yet loaded')
      return def
    } else {
      return (await this._serverData) || def
    }
  }

  public pageView(location: WidgetRuntime['location']) {
    // record visit and store data

    //
    const now = new Date().getTime()

    // utm
    const utm = this.refreshUtm(location)

    // page number
    const page = this.pageNum + 1
    this.storages.saveSession('pageNum', page)

    this._visit = {
      userId: this.userId,
      sessionId: this.sessionId,
      sessionStartTime: this.sessionStartTime.getTime(),

      time: now,
      location: location().href,
      referrer: window.document.referrer,
      pageNumber: page,
      sessionNumber: this.storages.load('sessionsCount') || 1,
      lastVisit: this.storages.loadSession('lastVisit'),

      source: this.storages.loadSession('source'),
      utm,
      language: getLanguage(),

      displayWidth: window.screen.width,
      displayHeight: window.screen.height,
    }

    // save last visit
    this.storages.saveSession('lastVisit', this._visit.time)
  }

  private refreshUtm(location: WidgetRuntime['location']): UTM {
    const savedUtm = (this.storages.load('utm') || {}) as UTM
    const locationUtm = parseUtm(location)

    let utm: UTM = {}
    let save = false
    if (locationUtm && Object.keys(locationUtm).length) {
      utm = { ...locationUtm, method: UTMMethod.Url }
      save = true
    }

    if (Object.keys(utm).length && (save || !Object.keys(savedUtm).length)) {
      this.storages.save('utm', utm)
    } else if (Object.keys(savedUtm).length) {
      utm = { method: UTMMethod.Url, ...savedUtm }
    }

    return utm
  }

  private refreshUtmServer(sourceUtm: SessionServerData['utm']) {
    const utm: UTM = {}
    if (sourceUtm.medium) {
      utm.utm_medium = sourceUtm.medium
    }
    if (sourceUtm.source) {
      utm.utm_source = sourceUtm.source
    }

    const saved = this.storages.load('utm') as UTM
    if (Object.keys(utm).length) {
      if (!saved || !Object.keys(saved).length) {
        utm.method = UTMMethod.Referrer
        this.storages.save('utm', utm)
        if (this._visit) {
          this._visit.utm = utm
        }
      }
    }
  }

  private updateId(userId: string, sessionId: string) {
    if (this.userId != userId) {
      this.userId = userId
      this.storages.save('uid', this.userId)
    }
    this.sessionId = sessionId
    this.storages.saveSession('id', sessionId)

    this._visit.userId = userId
    this._visit.sessionId = sessionId
  }

  public get pageNum(): number {
    return this.storages.loadSession('pageNum') || 0
  }

  public get source(): string {
    return this.storages.loadSession('source')
  }

  public get landing(): string {
    return this.storages.loadSession('landing')
  }

  public get currentVisit(): Visit {
    return this._visit
  }
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_term', 'utm_content', 'utm_campaign']

export function parseUtm(location: WidgetRuntime['location']): UTM {
  const query = parseQuery(location().search)
  const hashQuery = parseQuery(location().hash.substring(1))
  const utm: UTM = {}
  UTM_KEYS.forEach((k: keyof UTM) => {
    const v = query[k] || hashQuery[k]
    if (v) {
      utm[k] = v
    }
  })
  return utm
}

function getLanguage(): string {
  return navigator.language.split('-')[0]
}
